import type { AppCheckToken } from "firebase/app-check";
import { sendMessageToFlutter } from "../bridge";
import { FlutterChannel } from "../enums/channel";
import { FlutterEvents } from "../enums/events";

let inFlightPromise: Promise<AppCheckToken> | null = null;

/**
 * Extracts expiration milliseconds from the App Check JWT payload's exp claim.
 */
export function parseJwtExpirationMillis(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length >= 2) {
      const base64Url = parts[1];
      const base64 = base64Url!.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );
      const parsed = JSON.parse(jsonPayload);
      if (typeof parsed.exp === "number") {
        return parsed.exp * 1000;
      }
    }
  } catch {
    // Fall back to provided expireTimeMillis if JWT parsing fails
  }
  return null;
}

/**
 * Requests a native App Check token from the Flutter wrapper.
 * Deduplicates concurrent in-flight requests and handles timeouts and error events gracefully.
 */
export async function requestNativeAppCheckToken(
  forceRefresh?: boolean,
): Promise<AppCheckToken> {
  // Deduplicate in-flight requests when not forcing a refresh
  if (inFlightPromise && !forceRefresh) {
    return inFlightPromise;
  }

  inFlightPromise = new Promise((resolve, reject) => {
    // 10-second safety timeout so the promise never hangs indefinitely
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error("Native App Check token request timed out"));
    }, 10000);

    function cleanup() {
      window.removeEventListener(
        FlutterEvents.NEW_NATIVE_APP_CHECK_TOKEN,
        handleSuccess,
      );
      window.removeEventListener(
        FlutterEvents.NEW_NATIVE_APP_CHECK_TOKEN_ERROR,
        handleError,
      );
      clearTimeout(timeoutId);
      inFlightPromise = null;
    }

    function handleSuccess(event: Event) {
      cleanup();
      const customEvent = event as CustomEvent<{
        token: string;
        expireTimeMillis?: number;
      }>;
      const token = customEvent.detail.token;
      // Extract expiration from JWT `exp` claim. If unavailable, fall back to Flutter-provided
      // expireTimeMillis (for backward compatibility with older wrapper versions) or default to 55m TTL.
      const expireTimeMillis =
        parseJwtExpirationMillis(token) ??
        (Number(customEvent.detail?.expireTimeMillis) ||
          Date.now() + 55 * 60 * 1000);

      const appCheckToken: AppCheckToken = {
        token,
        expireTimeMillis,
      };

      resolve(appCheckToken);
    }

    function handleError(event: Event) {
      cleanup();
      const customEvent = event as CustomEvent<{ error?: string }>;
      reject(
        new Error(
          customEvent.detail?.error ||
            "Failed to retrieve native App Check token",
        ),
      );
    }

    window.addEventListener(
      FlutterEvents.NEW_NATIVE_APP_CHECK_TOKEN,
      handleSuccess,
    );
    window.addEventListener(
      FlutterEvents.NEW_NATIVE_APP_CHECK_TOKEN_ERROR,
      handleError,
    );

    // Ask Flutter for token
    sendMessageToFlutter(
      FlutterChannel.REQUEST_NATIVE_APP_CHECK_TOKEN,
      {
        forceRefresh: !!forceRefresh,
      },
      (err) => {
        cleanup();
        reject(err);
      },
    );
  });

  return inFlightPromise;
}
